import React, { useRef } from 'react';
import styled from 'styled-components';
import { ButtonSecondary, Type, screenXsMax, spacer16 } from '@spotify-internal/encore-web/core';

import { CallToActionCardSchema } from 'lib/sanity/types';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import { Vertical } from 'utils/vertical';
import { UbiArticlePage } from 'utils/ubi/pages/useUbiArticle';
import useIsIntersectingEffect from 'utils/ubi/useIsIntersectingEffect';

import StyledCard from '../StyledCard';

const StyledContentContainer = styled.div`
  margin-top: ${spacer16};
`;

// `order` is used for placement(top/bottom) here
const StyledOrderedFlexItem = styled.div<{ $order: number }>`
  order: ${props => props.$order};
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;

  @media (max-width: ${screenXsMax}) {
    grid-template-columns: 1fr;
    gap: ${spacer16};
  }
`;

const StyledTextContainer = styled.div`
  align-self: center;
`;

const StyledButtonContainer = styled.div`
  text-align: right;
  align-self: center;
`;

// To make it simple from the beginning
// CTA layouts are bound with verticals
// So we don't need another field in the schema
// TODO: Add placement and layout into schema if necessary
const CallToActionCard = ({
  title,
  content,
  buttonLink,
  buttonText,
  ubi,
  _id: identifier,
}: CallToActionCardSchema & { ubi: UbiArticlePage }) => {
  const ctaRef = useRef(null);
  const {
    verticalConfig: { verticalName },
  } = useServerSideConstants();
  const isConsumer = verticalName === Vertical.CONSUMER;

  useIsIntersectingEffect(ctaRef, (isVisible: boolean) => isVisible && ubi.logCTAImpression(identifier));

  return (
    <StyledOrderedFlexItem $order={isConsumer ? -1 : 1} ref={ctaRef}>
      <StyledCard $noMarginTop={isConsumer} className={isConsumer ? '' : 'encore-bright-accent-set'}>
        <StyledGrid>
          <StyledTextContainer>
            {title && (
              <Type as="div" variant="balladBold" semanticColor="textBase">
                {title}
              </Type>
            )}

            {content && (
              <StyledContentContainer>
                <Type as="p" variant="ballad" semanticColor="textBase">
                  {content}
                </Type>
              </StyledContentContainer>
            )}
          </StyledTextContainer>

          {buttonText && buttonLink && (
            <StyledButtonContainer>
              <ButtonSecondary
                as="a"
                href={buttonLink}
                size="small"
                semanticColor="textBase"
                onClick={() => {
                  ubi.logCTALinkInteraction(buttonLink, identifier);
                }}
              >
                {buttonText}
              </ButtonSecondary>
            </StyledButtonContainer>
          )}
        </StyledGrid>
      </StyledCard>
    </StyledOrderedFlexItem>
  );
};

export default CallToActionCard;
